import { UserRound } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { routeUrl } from "@/app/routes";
import { Permission } from "@/shared/auth/permissions";
import { useCanDo } from "@/shared/auth/use-can-do";
import type { CommandEntry } from "@/shared/components/command-menu/command-match";
import { useActiveWorkspace } from "@/shared/hooks/use-active-workspace";
import type { DirectoryRow } from "@/shared/membership/directory.schema";
import { usePeopleList } from "@/shared/membership/use-people-list";

export const PEOPLE_MIN_TERM = 2;
export const PEOPLE_LIMIT = 8;
/// Same interval every other search-driven picker uses (membership-picker).
export const PEOPLE_DEBOUNCE_MS = 300;

export function shouldSearchPeople({
  term,
  canListMembers,
}: {
  term: string;
  canListMembers: boolean;
}): boolean {
  return canListMembers && term.trim().length >= PEOPLE_MIN_TERM;
}

export function personHint(row: DirectoryRow): string {
  const parts = [row.title, row.departmentName].filter((part): part is string => Boolean(part));
  return parts.length > 0 ? parts.join(", ") : row.email;
}

export function toPersonCommand(row: DirectoryRow, workspaceSlug: string): CommandEntry {
  const hint = personHint(row);
  return {
    id: `person:${row.membershipId}`,
    kind: "person",
    label: row.displayName,
    href: routeUrl.people.detail(workspaceSlug, row.membershipId),
    icon: UserRound,
    // Imports sometimes set the job title to the person's own name, and a row
    // reading "Ada Lovelace ... Ada Lovelace" looks like a rendering bug.
    hint: hint === row.displayName ? undefined : hint,
    avatarUrl: row.avatarUrl,
  };
}

/// People come from a server search rather than client-side filtering of a
/// prefetched roster: the roster read caps at 100 rows, so person 101 would be
/// silently unfindable. Passing `undefined` as the workspaceId is how the
/// underlying list hook's `enabled` gate is switched off (useListResource), so
/// no request fires before the term is worth searching.
export function useCommandPeople(term: string): {
  people: CommandEntry[];
  isPending: boolean;
  isError: boolean;
} {
  const { workspace, isResolved } = useActiveWorkspace();
  const canListMembers = useCanDo(Permission.WORKSPACE_LIST_MEMBERS);
  const [debouncedTerm, setDebouncedTerm] = useState("");
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setDebouncedTerm(term), PEOPLE_DEBOUNCE_MS);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [term]);

  const enabled = isResolved && shouldSearchPeople({ term: debouncedTerm, canListMembers });

  const query = usePeopleList(enabled ? workspace.id : undefined, {
    search: debouncedTerm.trim(),
    limit: PEOPLE_LIMIT,
    // Pending invitees have no profile page worth landing on.
    status: "active",
    sortBy: "displayName",
    sortDir: "asc",
  });

  if (!enabled) {
    return { people: [], isPending: false, isError: false };
  }

  return {
    people: (query.data?.rows ?? []).map((row) => toPersonCommand(row, workspace.slug)),
    // A term that is typed but not yet debounced is still "pending" from the
    // searcher's point of view, so the skeleton shows immediately rather than
    // 300ms later.
    isPending: query.isPending || debouncedTerm !== term,
    isError: query.isError,
  };
}
